import {
    FCX,
    getScaledPixels,
    useWindowSizeDebounced
} from "@datassential/platform-ui-lib";
import { memo, useEffect, useMemo, useRef } from "react";
import cn from "classnames";
import { FixedSizeList } from "react-window";
import ItemTile from "@vms/ItemTile";
import WindowScroller from "@components/WindowScroller";
import { useHomeContext } from "@pages/Home/HomeContext";
import { HomeTileDataItem } from "@api/useHomeTilesData";
import { useFilteredKeywordsData } from "@api/useFilteredKeywordsData";
import { useFilterSort } from "@hooks/useFilterSort";
import { Sort } from "@models";
import { ITEM_TILE_DEFAULT_HEIGHT } from "@vms/ItemTile/ItemTile";

const Tiles: FCX<{
    tileHeight?: number;
    isLoading: boolean;
    rawData: HomeTileDataItem[];
}> = ({
    className,
    isLoading,
    rawData,
    tileHeight = ITEM_TILE_DEFAULT_HEIGHT,
}) => {
    const listRef = useRef<HTMLDivElement>(null);
    const [wWidth, wHeight] = useWindowSizeDebounced();

    const {
        search,
        sortContext,
        setSortContext,
        setResultCount,
    } = useHomeContext();

    const { getFinalData, dataForSorting } = useFilteredKeywordsData(rawData, search);

    useEffect(() => {
        setResultCount(dataForSorting.length);
    }, [dataForSorting, setResultCount]);

    const mappedDataForSorting = useMemo(
        () => dataForSorting.map(i => ({
            ...i,
            ...i.metrics.reduce(
                (acc, metric) => ({
                    ...acc,
                    [`metric${metric.id}`]: metric.sortValue,
                }),
                {}
            ),
        })),
        [dataForSorting]
    );

    const {
        data: sortedData,
        sortingKey,
        setSortingKey,
        sortingDirection,
        setSortingDirection,
    } = useFilterSort(
        mappedDataForSorting,
        'name',
        Sort.Asc,
        ['name']
    );

    useEffect(() => {
        setSortContext({
            sortKey: sortingKey,
            setSortKey: setSortingKey as (typeof sortContext)['setSortKey'],
            sortDirection: sortingDirection,
            setSortDirection: setSortingDirection,
        });
    }, [sortingKey, sortingDirection, setSortingKey, setSortingDirection, setSortContext]);

    const finalData = useMemo(
        () => getFinalData(sortedData),
        [getFinalData, sortedData]
    );

    const data = useMemo(() => isLoading ? Array(30).fill(null) : finalData, [isLoading, finalData]);

    const scaledTileHeight = useMemo(() => getScaledPixels(tileHeight + 16, wWidth), [tileHeight, wWidth]);

    const tilesPerRow = useMemo(() => {
        if (wWidth >= 1600) {
            return 5;
        } else {
            return 4;
        }
    }, [wWidth]);

    const rowsCount = useMemo(
        () => Math.ceil(data.length / tilesPerRow),
        [tilesPerRow, data]
    );

    const itemsRows = useMemo(() => {
        return Array(rowsCount).fill(null).map((_, rowIndex) => {
            const startIndex = rowIndex * tilesPerRow;
            const endIndex = startIndex + tilesPerRow;
            return data.slice(startIndex, endIndex);
        });
    }, [data, tilesPerRow, rowsCount]);

    return (
        <div>
            <WindowScroller scrollerRef={listRef}/>
            {!isLoading && rowsCount === 0 && (
                <div className="HomeTilesView__fallback">
                    No matching items found
                </div>
            )}
            <FixedSizeList
                // @ts-ignore
                ref={listRef}
                itemSize={scaledTileHeight}
                width="100%"
                height={wHeight}
                itemCount={rowsCount}
                className={
                    cn(
                        "HomeTilesView",
                        "window-scroller-override",
                        className
                    )
                }
            >
                {({ index, style }) => (
                    <div className="HomeTilesView__item-row" style={style}>
                        {itemsRows[index].map((item, index) => (
                            <ItemTile
                                key={`${item?.id}-${index}`}
                                className="HomeTilesView__item-tile"
                                item={item}
                                tileHeight={scaledTileHeight}
                                isLoading={isLoading}
                                isSelectionEnabled
                            />
                        ))}
                    </div>
                )}
            </FixedSizeList>
        </div>
    );
};

export default memo(Tiles);